/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { XMLParser } from 'fast-xml-parser';
import './index.css';
import { useState } from 'react';
import styles from './custom.module.css';
import Header from './components/Header';
import SelectOption from './components/SelectOption';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Dialog, DialogActions, DialogTitle } from 'nxg-ui-wrapper';
import { DialogContent } from '@mui/material';

const MITSUBISHI_VENDOR = 'mitsubishi';
const YASKAWA_VENDOR = 'yaskawa';
const ORIENTAL_VENDOR = 'oriental';
const SHINMEIWA_VENDOR = 'shinmeiwa';
const fileNameList = [
    {
        vendor: MITSUBISHI_VENDOR,
        fileNames: [
            { exportFile: 'template_mitsu_mr_j5_g_n1', rawFileName: 'MELSERVO MR_J5_G_N1.xml' },
            { exportFile: 'template_mitsu_mr_j5w3_g_n1', rawFileName: 'MELSERVO MR_J5W3_G_N1.xml' },
            { exportFile: 'template_mitsu_mr_j5w2_g_n1', rawFileName: 'MELSERVO MR_J5W2_G_N1.xml' },
            { exportFile: 'template_mitsu_mr_j5_g_rj042n1', rawFileName: 'MELSERVO MR_J5_G_RJ042N1.xml' },
            { exportFile: 'template_mitsu_mr_j5_g_rjn1', rawFileName: 'MELSERVO MR_J5_G_RJN1.xml' },
        ],
    },
    {
        vendor: YASKAWA_VENDOR,
        fileNames: [{ exportFile: 'template_yas', rawFileName: 'YASKAWA JOHB-SMP3_3.02.xml' }],
    },
    {
        vendor: ORIENTAL_VENDOR,
        fileNames: [{ exportFile: 'template_ori_cvd4ar_ked', rawFileName: 'SWE562-308-08_ORIENTALMOTOR_CVD4A-KED_rev0000.xml' }],
    },
    {
        vendor: SHINMEIWA_VENDOR,
        fileNames: [{ exportFile: 'template_shinmeiwa_svph332a2_007', rawFileName: 'NikkiDenso NCR-HD Series.xml' }],
    },
];
const DEFAULT_SELECTED_NAME = 'Please select the device';
const dataTypeMapping = {
    SINT: 'int8',
    USINT: 'uint8',
    INT: 'int16',
    UINT: 'uint16',
    DINT: 'int32',
    UDINT: 'uint32',
    LINT: 'int64',
    ULINT: 'uint64',
};

const TOOL_TITLE = 'ESI File Parser';

const PROFILE_AREA_INDEX = [[0x605a, 0x605d], 0x6060, 0x6065, [0x607c, 0x607d], 0x607f, 0x6081, [0x6083, 0x6085], [0x6091, 0x609a]];
const MANUFACTURE_INDEX = [
    [0x4120, 0x413a],
    [0x4142, 0x41da],
    [0x4700, 0x493f],
];

const PLEASE_SELECT_CHILD_FILE_TEXT = 'Please provide file:';
function AppContextComponent(): JSX.Element {
    const [parsedData, setParsedData] = useState<string>('');
    const [deviceList, setDeviceList] = useState<any[]>([]);
    const [vendor, setVendor] = useState<string>('');
    const [childFile, setChildFile] = useState<string>('');
    const [exportFileName, setExportFileName] = useState<string>('');
    const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
    const [selectedDeviceName, setSelectedDeviceName] = useState<string>(DEFAULT_SELECTED_NAME);
    const [selectedDeviceNo, setSelectedDeviceNo] = useState<number>(0);
    const [deviceRevision, setDeviceRevision] = useState<string>('');
    const [hasError, setHasError] = useState<boolean>(false);

    const downloadFile = (filename: string, content: string): void => {
        const blob = new Blob([content], { type: 'application/xml' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        try {
            setHasError(false);
            setChildFile('');
            setSelectedDeviceName(DEFAULT_SELECTED_NAME);
            setParsedData('');
            setDeviceList([]);
            const file = e.target.files?.[0];
            let vendorTmp = '';
            if (!file) return;
            const text = await file.text();

            if (text) {
                const options = {
                    ignoreAttributes: false,
                    attributeNamePrefix: '',
                    textNodeName: 'text',
                };
                const parser = new XMLParser(options);
                const xmlObject = parser.parse(text);
                fileNameList.forEach(listObj => {
                    const matchFile = listObj.fileNames.find(fileConfig => fileConfig.rawFileName === file.name);
                    if (matchFile) {
                        vendorTmp = listObj.vendor;
                        setExportFileName(matchFile.exportFile);
                    }
                });
                const devices = xmlObject.EtherCATInfo.Descriptions.Devices.Device;
                setDeviceList(Array.isArray(devices) ? devices : [devices]);
                setVendor(vendorTmp);
            }
        } catch (error) {
            console.error(error);
            setHasError(true);
        }
    };

    function extractYaskawaModel(input: string): string | null {
        const regex = /YASKAWA\s+([A-Za-z0-9]+)\s+series/i;
        const match = input.match(regex);
        return match ? match[1] : null; // return model name or null if not found
    }

    const handleDeviceChange = (deviceNo: number, revision: string): void => {
        try {
            let data: any = [];
            const supportedRawDataType = Object.keys(dataTypeMapping);
            if (vendor === MITSUBISHI_VENDOR) {
                data = deviceList[deviceNo].Profile.Dictionary.Objects.Object.filter((item: any) => item.Name.match('^P[A-V].*$')).reduce(
                    (resultList: any, rootItem: any) => {
                        if (!supportedRawDataType.includes(rootItem.Type)) {
                            const type = deviceList[deviceNo].Profile.Dictionary.DataTypes.DataType.find(type => type.Name === rootItem.Type);
                            if (type?.SubItem.length) {
                                const rwItems = type?.SubItem.filter(item => item?.Flags.Access === 'rw');
                                rwItems.forEach(item => {
                                    const type = deviceList[deviceNo].Profile.Dictionary.DataTypes.DataType.find(type => type.Name === item.Type);
                                    if (type.ArrayInfo) {
                                        Array.from({ length: type.ArrayInfo.Elements }).forEach((_, index) => {
                                            resultList.push({
                                                name: 'PV' + `${index + 1}`.padStart(2, '0'),
                                                index: `${rootItem.Index}`.replace('#', '0'),
                                                subindex: numberToHex(index + 1, 2),
                                                data_type: dataTypeMapping[type.BaseType],
                                            });
                                        });
                                    }
                                });
                            }
                        } else {
                            resultList.push({
                                name: rootItem.Name,
                                index: `${rootItem.Index}`.replace('#', '0'),
                                subindex: '0x00',
                                data_type: dataTypeMapping[rootItem.Type],
                            });
                        }

                        return resultList;
                    },
                    [],
                );
            }
            setSelectedDeviceNo(deviceNo);
            if (vendor === YASKAWA_VENDOR) {
                const [_, fileName] = deviceList[deviceNo].Profile.DictionaryFile.split('\\');
                setChildFile(fileName);
                setIsOpenDropdown(false);
                setDeviceRevision(revision);
                setSelectedDeviceName(deviceList[deviceNo]?.Name[0]?.text ?? deviceList[deviceNo]?.Name?.text ?? deviceList[deviceNo]?.Name);
                setExportFileName(
                    `${exportFileName}_${extractYaskawaModel(
                        deviceList[deviceNo]?.Name[0]?.text ?? deviceList[deviceNo]?.Name?.text ?? deviceList[deviceNo]?.Name,
                    )?.toLowerCase()}`,
                );
                return;
            }
            if (vendor === ORIENTAL_VENDOR) {
                const indexObj: any = deviceList[deviceNo].Profile.Dictionary.Objects.Object.reduce((result: any, item: any) => {
                    const [_, hexPart] = item.Index.split('x');
                    result[`0x${hexPart}`] = item;
                    return result;
                }, {});
                const typeObject: any = deviceList[selectedDeviceNo].Profile.Dictionary.DataTypes.DataType.reduce((resultObj, obj) => {
                    resultObj[obj.Name] = obj;
                    return resultObj;
                }, {});
                MANUFACTURE_INDEX.forEach(range => {
                    for (let i = range[0]; i <= range[1]; i++) {
                        const hexIndex = `0x${i.toString(16).toUpperCase().padStart(4, '0')}`;
                        if (
                            indexObj[hexIndex] &&
                            supportedRawDataType.includes(indexObj[hexIndex]?.Type) &&
                            indexObj[hexIndex]?.Flags.Access === 'rw'
                        ) {
                            const pushedObject = {
                                name: indexObj[hexIndex]?.Name,
                                index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                subindex: '0x00',
                                data_type: dataTypeMapping[indexObj[hexIndex]?.Type],
                            };
                            data.push(pushedObject);
                        } else {
                            typeObject[indexObj[hexIndex]?.Type]?.SubItem.forEach(dataType => {
                                if (dataType.SubIdx > 0 && dataType?.Flags.Access === 'rw') {
                                    const pushedObject = {
                                        name: dataType?.Name,
                                        index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                        subindex: numberToHex(dataType.SubIdx, 2),
                                        data_type: dataTypeMapping[dataType?.Type],
                                    };
                                    data.push(pushedObject);
                                }
                            });
                        }
                    }
                });
                for (let j = 0; j < 4; j++) {
                    PROFILE_AREA_INDEX.forEach(indexes => {
                        if (Array.isArray(indexes)) {
                            const start = indexes[0] + j * 0x800;
                            const end = indexes[1] + j * 0x800;
                            for (let i = start; i <= end; i++) {
                                const hexIndex = `0x${i.toString(16).toUpperCase().padStart(4, '0')}`;
                                if (indexObj[hexIndex] && supportedRawDataType.includes(indexObj[hexIndex]?.Type)) {
                                    const pushedObject = {
                                        name: indexObj[hexIndex]?.Name,
                                        index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                        subindex: '0x00',
                                        data_type: dataTypeMapping[indexObj[hexIndex]?.Type],
                                    };
                                    data.push(pushedObject);
                                } else {
                                    typeObject[indexObj[hexIndex]?.Type]?.SubItem.forEach(dataType => {
                                        if (dataType.SubIdx > 0) {
                                            const pushedObject = {
                                                name: dataType?.Name,
                                                index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                                subindex: numberToHex(dataType.SubIdx, 2),
                                                data_type: dataTypeMapping[dataType?.Type],
                                            };
                                            data.push(pushedObject);
                                        }
                                    });
                                }
                            }
                        } else {
                            const currentIndex = indexes + j * 0x800;
                            const hexIndex = `0x${currentIndex.toString(16).toUpperCase().padStart(4, '0')}`;
                            if (indexObj[hexIndex] && supportedRawDataType.includes(indexObj[hexIndex]?.Type)) {
                                const pushedObject = {
                                    name: indexObj[hexIndex]?.Name,
                                    index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                    subindex: '0x00',
                                    data_type: dataTypeMapping[indexObj[hexIndex]?.Type],
                                };
                                data.push(pushedObject);
                            } else {
                                typeObject[indexObj[hexIndex]?.Type]?.SubItem.forEach(dataType => {
                                    if (dataType.SubIdx > 0) {
                                        const pushedObject = {
                                            name: dataType?.Name,
                                            index: indexObj[hexIndex]?.Index.replace('#', '0'),
                                            subindex: numberToHex(dataType.SubIdx, 2),
                                            data_type: dataTypeMapping[dataType?.Type],
                                        };
                                        data.push(pushedObject);
                                    }
                                });
                            }
                        }
                    });
                }
            }

            if (vendor === SHINMEIWA_VENDOR) {
                const indexObj: any = deviceList[deviceNo].Profile.Dictionary.Objects.Object.reduce((result: any, item: any) => {
                    const [_, hexPart] = item.Index.split('x');
                    result[`0x${hexPart}`] = item;
                    return result;
                }, {});
                const start = 0x3000;
                const end = 0x33e7;
                for (let i = start; i <= end; i++) {
                    const hexIndex = `0x${i.toString(16).toUpperCase().padStart(4, '0')}`;
                    const paramName = `P${(i - start).toString().padStart(3, '0')}`;

                    if (indexObj[hexIndex]) {
                        data.push({
                            name: paramName,
                            index: hexIndex,
                            subindex: '0x00',
                            data_type: dataTypeMapping[indexObj[hexIndex]?.Type],
                        });
                    }
                }
            }
            setParsedData('[\n' + data.map(obj => '  ' + JSON.stringify(obj).replace(/,/g, ', ')).join(',\n') + '\n]');
            setIsOpenDropdown(false);
            setSelectedDeviceName(deviceList[deviceNo]?.Name[0]?.text ?? deviceList[deviceNo]?.Name?.text ?? deviceList[deviceNo]?.Name);
            setDeviceRevision(revision);
        } catch (err) {
            console.error(err);
        }
    };
    function numberToHex(num: number, pad = 0): string {
        let hex = num.toString(16).toUpperCase();
        if (Number.isInteger(num)) {
            if (pad > 0) {
                hex = hex.padStart(pad, '0');
            }
        }
        return '0x' + hex;
    }

    const handleGetChildFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = e.target.files?.[0];
        let data = '';
        if (!file) return;
        const text = await file.text();

        if (text) {
            const options = {
                ignoreAttributes: false,
                attributeNamePrefix: '',
                textNodeName: 'text',
            };
            const parser = new XMLParser(options);
            const xmlObject = parser.parse(text);
            const mappingData = xmlObject.EtherCATDict.Dictionary.Objects.Object.filter((item: any) =>
                item.Name.match(/\b([A-Za-z0-9]{2})-parameter group/),
            ).reduce((finalList: any, item: any) => {
                const dataType = xmlObject.EtherCATDict.Dictionary.DataTypes.DataType.find(type => type.Name === item.Type);
                dataType?.SubItem.forEach((sub: any): any => {
                    if (sub?.Flags.Access === 'rw' && sub?.Name !== 'No parameter') {
                        finalList.push({
                            name: sub.Name,
                            index: `${item.Index}`.replace('#', '0'),
                            subindex: numberToHex(sub.SubIdx, 2),
                            data_type: dataTypeMapping[sub.Type],
                        });
                    }
                });
                return finalList;
            }, []);
            data = '[\n' + mappingData.map(obj => '  ' + JSON.stringify(obj).replace(/,/g, ', ')).join(',\n') + '\n]';
        }
        setParsedData(data);
    };
    const handleToggle = (): void => setIsOpenDropdown(!isOpenDropdown);

    return (
        <div className={styles.toolWrapper}>
            <Header />
            <div className={styles.parserWrapper}>
                <h2 className={styles.toolTitle}>{TOOL_TITLE}</h2>
                <div className={styles.toolBody}>
                    <div className={styles.inputFileWrapper}>
                        <input type="file" onChange={handleFileChange} />
                    </div>
                    <div className={styles.deviceSelectWrapper}>
                        <div className={styles.selectWrapper} onClick={handleToggle}>
                            <div>{selectedDeviceName}</div>
                            {!isOpenDropdown ? <ArrowRightIcon /> : <ArrowDropDownIcon />}
                        </div>
                        {isOpenDropdown && (
                            <div className={styles.optionWrapper}>
                                {deviceList?.map((device: any, index: number) => (
                                    <SelectOption
                                        deviceNo={index}
                                        name={device.Name[0]?.text ?? device.Name.text ?? device.Name}
                                        productCode={device?.Type?.ProductCode.replace('#', '0')}
                                        revision={device?.Type?.RevisionNo.replace('#', '0')}
                                        image={device?.ImageData16x14}
                                        onClick={handleDeviceChange}
                                    />
                                ))}
                            </div>
                        )}
                        {!deviceList.length && <div className={styles.selectOverlay}></div>}
                    </div>

                    {childFile && (
                        <>
                            <span style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
                                {PLEASE_SELECT_CHILD_FILE_TEXT} {childFile}
                            </span>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <input type="file" onChange={handleGetChildFile} />
                            </div>
                        </>
                    )}
                </div>
                <Dialog open={hasError}>
                    <DialogTitle className={styles.errorDialogTitle}>Error</DialogTitle>
                    <DialogContent sx={{ pt: 2, pr: 3, pb: 0, pl: '10px' }}>Can not parse this file</DialogContent>
                    <DialogActions>
                        <button className={styles.errorDialogButton} onClick={(): void => setHasError(false)}>
                            Close
                        </button>
                    </DialogActions>
                </Dialog>

                <div className={styles.outputContentWrapper}>
                    <textarea className={styles.textAreaWrapper} value={parsedData}></textarea>
                    <div className={styles.footer}>
                        <button
                            className={styles.downloadButton}
                            onClick={(): void => downloadFile(`${exportFileName}_${deviceRevision}.json`, parsedData)}
                            disabled={!parsedData}
                        >
                            Download
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
    <BrowserRouter>
        <AppContextComponent></AppContextComponent>
    </BrowserRouter>,
);
